// Plain-text serializers for the Export feature — releases + features
// converted to markdown-ish text you can paste into an LLM.
//
// Format goals:
//   - readable to a human (markdown-ish headers + bullets)
//   - unambiguous to an LLM (clear section labels, no HTML)
//   - concise (only user-visible fields; skip DB ids, timestamps we don't
//     surface elsewhere, and empty fields)
import type {
  EnrichedFeature,
  FeedItem,
  Release,
  ReleaseItem,
} from "@release-hub/shared";
import { effectiveAnnouncementState } from "@release-hub/shared";
import { decodeProjectedGa, formatProjectedGa } from "./projectedGa";

const HR = "\n\n---\n\n";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function nonEmpty(s: string | null | undefined): s is string {
  return typeof s === "string" && s.trim().length > 0;
}

function line(label: string, value: string | null | undefined): string | null {
  return nonEmpty(value) ? `${label}: ${value}` : null;
}

function bulletList(items: string[]): string {
  return items.map((t) => `- ${t}`).join("\n");
}

// ─── Release ─────────────────────────────────────────────────────────

export function serializeRelease(release: Release): string {
  const build = release.builds?.[0];
  const tag = build?.tag_name ?? release.title ?? release.id;
  const repo = build?.repo ?? "";
  const date =
    release.release_date_override ??
    build?.published_at ??
    release.published_at ??
    release.created_at;

  const header = repo ? `# ${tag} — ${repo}` : `# ${tag}`;
  const meta: string[] = [];
  const publishedLine = line("Published", fmtDate(date));
  if (publishedLine) meta.push(publishedLine);
  if (release.status === "draft") meta.push("Status: draft");
  if ((release.builds?.length ?? 0) > 1) {
    const platforms = (release.builds ?? [])
      .map((b) => b.platform ?? b.surface)
      .filter(nonEmpty)
      .join(" + ");
    if (platforms) meta.push(`Platforms: ${platforms}`);
  }

  const parts: string[] = [header];
  if (meta.length) parts.push(meta.join("\n"));

  if (nonEmpty(release.narrative)) {
    parts.push(`## Narrative\n${release.narrative.trim()}`);
  }

  const items = release.items ?? [];
  if (items.length > 0) {
    parts.push(`## Items\n${items.map(serializeItem).join("\n")}`);
  }

  return parts.join("\n\n");
}

function serializeItem(item: ReleaseItem): string {
  const meta: string[] = [];
  if (nonEmpty(item.parity_notes)) meta.push(item.parity_notes.trim());
  if (item.linked_flag_ids.length > 0) meta.push(`flags: ${item.linked_flag_ids.join(", ")}`);
  if (item.linear_ticket_ids.length > 0) meta.push(`tickets: ${item.linear_ticket_ids.join(", ")}`);
  if (item.rollout_override) meta.push(`rollout: ${item.rollout_override}`);
  const suffix = meta.length ? ` (${meta.join("; ")})` : "";
  return `- [${item.category}] ${item.text.trim()}${suffix}`;
}

// ─── Feature ─────────────────────────────────────────────────────────

export function serializeFeature(feature: EnrichedFeature): string {
  const parts: string[] = [`# ${feature.name}`];

  // Meta block — one line per fact, only fields that have content.
  const meta: string[] = [];
  if (feature.beta) meta.push("Readiness: Beta");
  const familyLine = line("Family", feature.feature_family);
  if (familyLine) meta.push(familyLine);
  meta.push(`Rollout: ${feature.rollout.label} (${feature.rollout.kind})`);
  if (feature.flag_ids.length > 0) meta.push(`Flags: ${feature.flag_ids.join(", ")}`);
  if (feature.surface.length > 0) meta.push(`Surface: ${feature.surface.join(", ")}`);
  if (nonEmpty(feature.web_note)) meta.push(`Web: ${feature.web_note.trim()}`);
  if (nonEmpty(feature.mobile_note)) meta.push(`Mobile: ${feature.mobile_note.trim()}`);
  if (feature.status === "draft") meta.push("Status: draft");

  const effective = effectiveAnnouncementState(feature.announcement_state, feature.rollout);
  if (effective) {
    const label = effective === "IN_DEV" ? "Not yet available" : effective;
    meta.push(`Announcement: ${label}`);
  }
  if (nonEmpty(feature.ea_date) && feature.ea_date !== "TBD") {
    meta.push(`EA date: ${fmtDate(feature.ea_date)}`);
  }
  // Single GA target: a concrete date is a real GA date; a coarse value
  // (TBD/quarter/week) is a forward projection.
  const gaLabel = formatProjectedGa(feature.ga_date);
  if (gaLabel) {
    const isDate = decodeProjectedGa(feature.ga_date).kind === "date";
    meta.push(`${isDate ? "GA date" : "GA target"}: ${gaLabel}`);
  }

  const audienceLine = line("Audience", feature.audience);
  if (audienceLine) meta.push(audienceLine);
  const ownerLine = line("Owner", feature.owner_email);
  if (ownerLine) meta.push(ownerLine);

  parts.push(meta.join("\n"));

  if (nonEmpty(feature.pitch)) {
    parts.push(`## Pitch\n${feature.pitch.trim()}`);
  }
  if (nonEmpty(feature.announcement_narrative)) {
    parts.push(`## Announcement narrative\n${feature.announcement_narrative.trim()}`);
  }
  if (feature.known_gaps.length > 0) {
    parts.push(`## Known gaps\n${bulletList(feature.known_gaps)}`);
  }
  if (nonEmpty(feature.links_notes)) {
    parts.push(`## Helpful links / notes\n${feature.links_notes.trim()}`);
  }

  return parts.join("\n\n");
}

// ─── Feed items (Timeline) ────────────────────────────────────────────

export function serializeFeedItem(item: FeedItem): string {
  if (item.type === "release") return serializeRelease(item.data);
  if (item.type === "release_rollup") {
    // Rollups aren't individually selectable on the Timeline, so this is a
    // defensive fallback rather than a common path.
    return `${item.data.count} uncurated releases since ${item.data.since}`;
  }
  return serializeFeature(item.data);
}

// ─── Multi-item joiner ────────────────────────────────────────────────

export function joinExports(texts: string[]): string {
  return texts.join(HR);
}
